import { UserCog } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Steps, Callout, pathFor,
  Diagram, MockField, MockButton,
} from "../shared";

export default function AccountSection() {
  return (
    <div data-testid="guide-account">
      <PageHeading
        icon={UserCog}
        title="Your Account"
        description="Everything about getting into the system: signing in, changing your password, getting back in if you forget it, and what the system remembers about you."
      />

      <Callout variant="info" title="In plain words">
        Your account is your personal key to the system. Your administrator sets you up with a <span className="text-white">username</span> and <span className="text-white">password</span>. You use them to sign in, and you can change your password whenever you like.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Come here the first time you sign in, any time you want to change your password, or if you've forgotten it and need to get back in.
        </p>
      </Section>

      <Section title="Signing in">
        <p className="text-sm text-white/65 leading-relaxed">
          Go to the login page and type the username and password your administrator gave you. A sign-in lasts <span className="text-white">8 hours</span> — after that, the system asks you to sign in again to keep things secure.
        </p>
        <Diagram
          title="The login screen"
          frameLabel="/auth — Sign in"
          legend={[
            <>Type your <span className="text-white">username</span> here (this is not your email address).</>,
            <>Type your <span className="text-white">password</span>.</>,
            <>Click <span className="text-white">Sign in</span>.</>,
            <>Forgot it? Click <span className="text-white">"Forgot password?"</span> to start a reset.</>,
          ]}
        >
          <MockField label="Username" placeholder="e.g. amal.r" marker={1} />
          <MockField label="Password" value="••••••••" marker={2} />
          <MockButton label="Sign in" marker={3} />
          <p className="text-2xs text-cyan-300/80">
            <span className="inline-flex items-center gap-1"><span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">4</span> Forgot password?</span>
          </p>
        </Diagram>
        <Callout variant="info">
          Each time you sign in, the system gives you a fresh session, so any old "stay signed in" cookies stop working automatically. This is a safety feature — you don't need to do anything.
        </Callout>
      </Section>

      <Section title="Forgot your password">
        <p className="text-sm text-white/65 leading-relaxed">
          Click <span className="text-white">"Forgot password?"</span> on the login page and enter your username. If your account has an email address saved and email sending is switched on, the system emails you a one-time link to set a new password.
        </p>
        <Steps
          steps={[
            <>On the login page, click <span className="text-white">"Forgot password?"</span> (marker 4 above).</>,
            <>Enter your <span className="text-white">username</span> — not your email. For privacy, you'll see the same confirmation message either way, even if the username doesn't exist.</>,
            <>Check your inbox for an email titled <span className="text-white">"Password reset request"</span>. The link works for <span className="text-white">30 minutes</span>.</>,
            <>Click the link, type your new password (and type it again to confirm), then submit.</>,
            <>Sign in with your new password.</>,
          ]}
        />
        <Callout variant="warning" title="Only the newest link works">
          If you ask for a reset twice, only the most recent email works — the earlier link stops working. Always use the latest email.
        </Callout>
        <Callout variant="info">
          No email after a couple of minutes? Ask an admin to confirm your account has an email address saved, and that the IT Admin has set up email sending. The reset link always uses the system's official web address, not whatever address you happen to be visiting.
        </Callout>
      </Section>

      <Section title="Changing your password">
        <p className="text-sm text-white/65 leading-relaxed">
          For the full step-by-step, see <Link href={pathFor("forms/password")} className="text-cyan-300 hover:underline" data-testid="link-forms-password">Change / Reset Password</Link>.
        </p>
      </Section>

      <Section title="What the system remembers about you">
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2">
          <li>Your username, your role, an optional email address, and an optional link to your person record on the Team Members list.</li>
          <li>A scrambled version of your password — never the real thing. (The technical method is called bcrypt.)</li>
          <li>Your current sign-in session, which ends after 8 hours of no activity.</li>
          <li>A record, on every action item, of who did each thing — so your name is attached to the actions you take.</li>
        </ul>
      </Section>
    </div>
  );
}
